import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import StakingForm from './components/StakingForm';
import InvestmentsList from './components/InvestmentsList';
import TeamDetails from './components/TeamDetails';
import RankAchievements from './components/RankAchievements';
import AdminPanel from './components/AdminPanel';
import WelcomePage from './components/WelcomePage';
import { NETWORKS } from './utils/networks';
import { STAKING_ABI, ERC20_ABI } from './utils/contracts';
import './styles/App.css';

function App() {
  const [selectedNetwork, setSelectedNetwork] = useState('opBNBTestnet');
  const [account, setAccount] = useState('');
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [stakingContract, setStakingContract] = useState(null);
  const [tokenContract, setTokenContract] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [userInvestments, setUserInvestments] = useState([]);
  const [userIncomes, setUserIncomes] = useState(null);
  const [tokenBalance, setTokenBalance] = useState('0');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [referralId, setReferralId] = useState('');
  const [referralInfo, setReferralInfo] = useState(null);
  const [walletType, setWalletType] = useState(null);

  const currentNetwork = NETWORKS[selectedNetwork];

  // Extract referral ID from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refId = urlParams.get('ref') || urlParams.get('referral');
    if (refId) {
      setReferralId(refId);
    }

    // Listen for navigation events from WelcomePage
    const handleNavigateToStake = () => {
      setActiveTab('stake');
    };

    window.addEventListener('navigateToStake', handleNavigateToStake);

    return () => {
      window.removeEventListener('navigateToStake', handleNavigateToStake);
    };
  }, []);

  // Load referral info when we have a referral ID and contracts
  useEffect(() => {
    if (referralId && stakingContract) {
      loadReferralInfo(referralId);
    }
  }, [referralId, stakingContract]);

  const loadReferralInfo = async (refId) => {
    if (!stakingContract || !refId) return;
    
    try {
      const userInfo = await stakingContract.getUserInfo(refId);
      if (userInfo[0] !== '0x0000000000000000000000000000000000000000') {
        setReferralInfo({
          id: refId,
          wallet: userInfo[0],
          totalStaked: ethers.utils.formatEther(userInfo[4]),
          directReferrals: userInfo[7].toString()
        });
      }
    } catch (error) {
      console.error('Failed to load referral info:', error);
      setReferralInfo(null);
    }
  };

  const checkAdminStatus = async (stakingContract, userAddress) => {
    try {
      const owner = await stakingContract.owner();
      const admin = await stakingContract.admin();
      setIsAdmin(userAddress.toLowerCase() === owner.toLowerCase() || 
                 userAddress.toLowerCase() === admin.toLowerCase());
    } catch (error) {
      console.error('Failed to check admin status:', error);
      setIsAdmin(false);
    }
  };

  const loadUserData = async (stakingContract, tokenContract, userAddress) => {
    try {
      const userId = await stakingContract.getUserIdByAddress(userAddress);
      
      if (userId.toString() === '0') {
        setIsNewUser(true);
        setUserInfo(null);
        setUserInvestments([]);
        setUserIncomes(null);
      } else {
        setIsNewUser(false);
        
        const [userInfo, investments, incomes] = await Promise.all([
          stakingContract.getUserInfo(userId),
          stakingContract.getUserInvestments(userId),
          stakingContract.getUserIncomeBreakdown(userId)
        ]);

        setUserInfo({
          wallet: userInfo[0],
          id: userInfo[1].toString(),
          referrer: userInfo[2],
          referrerId: userInfo[3].toString(),
          totalStaked: ethers.utils.formatEther(userInfo[4]),
          totalReferrals: userInfo[5].toString(),
          teamVolume: ethers.utils.formatEther(userInfo[6]),
          directReferrals: userInfo[7].toString(),
          roiEnabled: userInfo[8],
          isBlacklisted: userInfo[9]
        });

        setUserInvestments(investments.map((inv, index) => ({
          index,
          planId: inv[0].toString(),
          amount: ethers.utils.formatEther(inv[1]),
          startTime: new Date(inv[2] * 1000),
          lastWithdraw: new Date(inv[3] * 1000),
          totalWithdrawn: ethers.utils.formatEther(inv[4]),
          capitalWithdrawn: inv[5],
          isActive: inv[6]
        })));

        setUserIncomes({
          referral: ethers.utils.formatEther(incomes[0]),
          roi: ethers.utils.formatEther(incomes[1]),
          level: ethers.utils.formatEther(incomes[2]),
          total: ethers.utils.formatEther(incomes[3])
        });
      }

      const balance = await tokenContract.balanceOf(userAddress);
      setTokenBalance(ethers.utils.formatEther(balance));
      
    } catch (error) {
      console.error('Failed to load user data:', error);
    }
  };

  // Wallet connection handlers
  const handleWalletConnect = async (connectedAccount, walletProvider, walletTypeValue) => {
    try {
      setLoading(true);
      setAccount(connectedAccount);
      setProvider(walletProvider);
      setWalletType(walletTypeValue);

      const web3Signer = walletProvider.getSigner();
      setSigner(web3Signer);

      const targetNetwork = NETWORKS[selectedNetwork];
      if (!targetNetwork) {
        throw new Error('Invalid network configuration');
      }

      const staking = new ethers.Contract(
        targetNetwork.contracts.staking,
        STAKING_ABI,
        web3Signer
      );
      const token = new ethers.Contract(
        targetNetwork.contracts.token,
        ERC20_ABI,
        web3Signer
      );

      setStakingContract(staking);
      setTokenContract(token);

      await loadUserData(staking, token, connectedAccount);
      await checkAdminStatus(staking, connectedAccount);

    } catch (error) {
      console.error('Failed to initialize contracts:', error);
      handleWalletError('Failed to initialize contracts: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleWalletDisconnect = () => {
    setAccount('');
    setProvider(null);
    setSigner(null);
    setWalletType(null);
    setStakingContract(null);
    setTokenContract(null);
    setUserInfo(null);
    setUserInvestments([]);
    setUserIncomes(null);
    setTokenBalance('0');
    setIsAdmin(false);
    setIsNewUser(false);
  };

  const handleWalletError = (errorMessage) => {
    console.error('Wallet error:', errorMessage);
    // You could add a toast notification here
  };

  const handleChainChanged = (chainId) => {
    // Check if the chain matches our expected network
    const expectedChainId = selectedNetwork === 'opBNB' ? 204 : 5611;
    if (chainId !== expectedChainId) {
      console.warn('Chain changed to unsupported network');
      // You could add a notification here
    }
  };

  // Network change handler for when user manually changes network
  useEffect(() => {
    // If user is connected and changes network, reinitialize contracts
    if (account && provider && selectedNetwork && NETWORKS[selectedNetwork]) {
      const reinitializeContracts = async () => {
        try {
          setLoading(true);
          const targetNetwork = NETWORKS[selectedNetwork];
          
          const staking = new ethers.Contract(
            targetNetwork.contracts.staking,
            STAKING_ABI,
            signer
          );
          const token = new ethers.Contract(
            targetNetwork.contracts.token,
            ERC20_ABI,
            signer
          );

          setStakingContract(staking);
          setTokenContract(token);

          await loadUserData(staking, token, account);
          await checkAdminStatus(staking, account);
        } catch (error) {
          console.error('Failed to reinitialize contracts:', error);
        } finally {
          setLoading(false);
        }
      };

      reinitializeContracts();
    }
  }, [selectedNetwork, account, provider, signer]);

  const refreshData = async () => {
    if (stakingContract && tokenContract && account) {
      await loadUserData(stakingContract, tokenContract, account);
    }
  };

  const renderContent = () => {
    // Show welcome page only if there's a referral ID and no account
    if (!account && referralId) {
      return (
        <WelcomePage 
          referralId={referralId}
          referralInfo={referralInfo}
          selectedNetwork={selectedNetwork}
          onWalletConnect={handleWalletConnect}
          onWalletDisconnect={handleWalletDisconnect}
          onWalletError={handleWalletError}
          onChainChanged={handleChainChanged}
        />
      );
    }

    switch (activeTab) {
      case 'dashboard':
        return (
          <Dashboard 
            userInfo={userInfo}
            userIncomes={userIncomes}
            tokenBalance={tokenBalance}
            isNewUser={isNewUser}
            account={account}
            stakingContract={stakingContract}
            refreshData={refreshData}
            selectedNetwork={selectedNetwork}
            setActiveTab={setActiveTab}
            onWalletConnect={handleWalletConnect}
            onWalletDisconnect={handleWalletDisconnect}
            onWalletError={handleWalletError}
            onChainChanged={handleChainChanged}
          />
        );
      case 'stake':
        return (
          <StakingForm 
            stakingContract={stakingContract}
            tokenContract={tokenContract}
            currentNetwork={currentNetwork}
            account={account}
            isNewUser={isNewUser}
            refreshData={refreshData}
            setLoading={setLoading}
            loading={loading}
            defaultReferralId={referralId}
          />
        );
      case 'investments':
        return (
          <InvestmentsList 
            userInvestments={userInvestments}
            stakingContract={stakingContract}
            refreshData={refreshData}
            setLoading={setLoading}
            loading={loading}
            userInfo={userInfo}
          />
        );
      case 'team':
        return (
          <TeamDetails 
            stakingContract={stakingContract}
            userInfo={userInfo}
            refreshData={refreshData}
          />
        );
      case 'rank':
        return (
          <RankAchievements 
            stakingContract={stakingContract}
            userInfo={userInfo}
            refreshData={refreshData}
          />
        );
      case 'admin':
        return isAdmin ? (
          <AdminPanel 
            stakingContract={stakingContract}
            tokenContract={tokenContract}
            currentNetwork={currentNetwork}
            refreshData={refreshData}
            setLoading={setLoading}
            loading={loading}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className="app">
      <Header 
        selectedNetwork={selectedNetwork}
        setSelectedNetwork={setSelectedNetwork}
        account={account}
        userInfo={userInfo}
        tokenBalance={tokenBalance}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        isAdmin={isAdmin}
        onWalletConnect={handleWalletConnect}
        onWalletDisconnect={handleWalletDisconnect}
        onWalletError={handleWalletError}
        onChainChanged={handleChainChanged}
      />
      
      <main className="main-content">
        {renderContent()}
      </main>
    </div>
  );
}

export default App;